import React, { FC, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  CardBody,
  CardFooter,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { Order, OrderRefund } from '../../../../shared/orders';
import { Currency } from '../../../components/Currency';
import { DateTime } from '../../../components/Date';
import { getRefund, getRefundRequestReason } from '../../../models';
import { useTranslation } from '../../../translations';
import { classNames } from '../../../utils';

enum RefundInfoTab {
  RequestInfo,
  RelatedPayments,
}

export const RefundInfo: FC<{
  readonly request: OrderRefund;
  readonly onAction: (type: 'approve' | 'reject') => void;
}> = ({ request, onAction }) => {
  const { ts } = useTranslation();
  const [infoTab, setInfoTab] = useState<RefundInfoTab>(1);

  const { order } = request;

  return (
    <>
      <CardHeader>
        <span>
          {ts('common.refund', request.id)} &mdash; Order #{order.id}
        </span>
      </CardHeader>
      <CardBody>
        <Nav className="mb-10" tabs>
          <NavItem>
            <NavLink
              className={classNames({ active: infoTab === RefundInfoTab.RequestInfo })}
              onClick={() => {
                setInfoTab(RefundInfoTab.RequestInfo);
              }}
            >
              {ts('attendee.refundinfo.request_info')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames({ active: infoTab === RefundInfoTab.RelatedPayments })}
              onClick={() => {
                setInfoTab(RefundInfoTab.RelatedPayments);
              }}
            >
              {ts('attendee.refundinfo.related_payments')}
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={infoTab}>
          <TabPane tabId={RefundInfoTab.RequestInfo}>
            <RequestInfo id={request.id} order={request.order} />
          </TabPane>
          <TabPane className="filter-table" tabId={RefundInfoTab.RelatedPayments}>
            {order.payments.map((payment) => (
              <div className="table-item" key={payment.id}>
                <p>
                  {ts('attendee.refundinfo.payment_paymentid', payment.id)} &mdash;{' '}
                  {payment.status === 'refunded' && '-'}
                  <Currency value={payment.amount} />
                  &nbsp;
                  {payment.status === 'success' ? (
                    <Badge color="success">{ts('common.success')}</Badge>
                  ) : (
                    <Badge color="secondary">{ts('common.refunded')}</Badge>
                  )}
                </p>
                <p>{ts('attendee.refundinfo.gateway_paymentgateway', payment.gateway)}</p>
                <p>{ts('attendee.refundinfo.paymentreference', payment.reference ?? 'n/a')}</p>
              </div>
            ))}
          </TabPane>
        </TabContent>
      </CardBody>
      <CardFooter>
        <ButtonGroup>
          <Button
            onClick={() => window.open(`/housekeeping/attendees/user/${order.userId}`, '_blank')}
          >
            {ts('profile')}
          </Button>
          {request.status === 'pending' && (
            <>
              <Button
                color="primary"
                id="approveRefund"
                onClick={() => {
                  onAction('approve');
                }}
              >
                {ts('common.approve')}
              </Button>
              <Button
                color="danger"
                id="rejectRefund"
                onClick={() => {
                  onAction('reject');
                }}
              >
                {ts('common.reject')}
              </Button>
            </>
          )}
        </ButtonGroup>
      </CardFooter>
    </>
  );
};

const RequestInfo: FC<{ readonly order: Order; readonly id: number }> = ({ order, id }) => {
  const refund = getRefund(order, id);

  const { ts } = useTranslation();
  return (
    <>
      <h6>{ts('attendee.refundinfo.requested_at')}</h6>
      <p>
        <DateTime value={refund.createdAt} />
      </p>

      <h6>{ts('attendee.refundinfo.reason_for_request')}</h6>
      <p>{getRefundRequestReason(refund.requestReason)}</p>

      <h6>{ts('common.additional_info')}</h6>
      <p>
        {refund.requestAddInfo ?? <i>{ts('attendee.refundinfo.no_additional_info_provided')}</i>}
      </p>
    </>
  );
};
